
import { defineComponent, onMounted, ref, inject, computed } from "vue";
import { useToast } from "vue-toastification";

import InputLabel from "@/components/atoms/InputLabel.vue";

// services
import emailService from "@/services/email.service";
import { AxiosError } from "axios";
import { useRoute, useRouter } from "vue-router";

export default defineComponent({
  components: {
    InputLabel,
  },
  setup() {
    const toast = useToast();
    const router = useRouter();
    const route = useRoute();

    const setIsLoading = inject("setIsLoading") as CallableFunction;

    // get email template
    const name = ref<string>("");
    const header = ref<string>("");
    const body = ref<string>("");
    const defaultName = ref<string>("");
    const defaultHeader = ref<string>("");
    const defaultBody = ref<string>("");

    const emailTemplateIdParam = String(route.params.id ?? "");

    const getEmailTemplates = async (id: string) => {
      try {
        setIsLoading(true);

        const { data } = await emailService.getEmailTemplate(id);

        name.value = data.name;
        header.value = data.subject;
        body.value = data.body;
        defaultName.value = data.name;
        defaultHeader.value = data.subject;
        defaultBody.value = data.body;
      } catch (e) {
        let errMsg = (e as AxiosError).response?.data?.error || "Ada kesalahan";
        toast.error(errMsg);
      } finally {
        setIsLoading(false);
      }
    };

    const getEmailTemplateDefault = async () => {
      try {
        setIsLoading(true);

        const { data } = await emailService.getDefaultEmailTemplate();

        body.value = data;
        defaultBody.value = data;
      } catch (e) {
        let errMsg = (e as AxiosError).response?.data?.error || "Ada kesalahan";
        toast.error(errMsg);
      } finally {
        setIsLoading(false);
      }
    };

    const EMPTY_BODY = "<p><br></p>";
    const canSubmit = computed(
      () =>
        name.value && header.value && body.value && body.value !== EMPTY_BODY
    );
    const reset = () => {
      name.value = defaultName.value;
      header.value = defaultHeader.value;
      body.value = defaultBody.value;
    };

    const goBack = () => {
      router.push({
        name: "CustomizeEmailList",
      });
    };

    const saveEmailTemplate = async (id: string) => {
      try {
        setIsLoading(true);

        const req = {
          name: name.value,
          subject: header.value,
          body: body.value,
        };

        await emailService.updateEmailTemplate(id, req);
        setIsLoading(false);

        toast.success("Template email berhasil diedit");

        router.push({
          name: "CustomizeEmailList",
        });
      } catch (e) {
        let errMsg = (e as AxiosError).response?.data?.error || "Ada kesalahan";
        toast.error(errMsg);
      }
    };

    const saveNewTemplate = async () => {
      try {
        setIsLoading(true);

        const req = {
          name: name.value,
          subject: header.value,
          body: body.value,
        };

        const { data } = await emailService.createEmailTemplate(req);
        setIsLoading(false);

        router.push({
          name: "CustomizeEmailList",
          params: {
            id: data.id,
          },
        });
      } catch (e) {
        let errMsg = (e as AxiosError).response?.data?.error || "Ada kesalahan";
        toast.error(errMsg);
      }
    };

    const saveEmail = async () => {
      if (emailTemplateIdParam) {
        await saveEmailTemplate(emailTemplateIdParam);
      } else {
        await saveNewTemplate();
      }
    };

    onMounted(async () => {
      if (emailTemplateIdParam) {
        await getEmailTemplates(emailTemplateIdParam);
      } else {
        await getEmailTemplateDefault();
      }
    });

    return {
      // data
      emailTemplateIdParam,
      name,
      header,
      body,

      // computed
      canSubmit,

      // methods
      reset,
      goBack,
      saveEmail,
    };
  },
});
